/**
 * Country Slug Translations — Maps translated URL slugs to country keys
 * Used to support localized URLs like /fr/centre-appel-france, /de/callcenter-deutschland
 * Non-Latin scripts (ar, zh, ja, ko, ru) keep English slugs to avoid URL encoding issues.
 */

// All country keys that exist in COUNTRY_PAGES
export type CountryKey =
  | 'morocco' | 'france' | 'germany' | 'spain' | 'uk' | 'usa' | 'canada'
  | 'netherlands' | 'belgium' | 'switzerland' | 'italy'
  | 'uae' | 'saudi-arabia' | 'qatar' | 'scandinavia' | 'portugal'
  | 'japan' | 'korea' | 'turkey' | 'brazil' | 'india'
  | 'australia' | 'south-africa' | 'mexico' | 'colombia' | 'poland'
  | 'philippines' | 'nigeria' | 'egypt' | 'saudi-arabia-expanded'
  | 'vietnam' | 'greece' | 'kenya' | 'thailand' | 'malaysia'
  | 'argentina' | 'ukraine' | 'sweden' | 'denmark' | 'russia' | 'china'
  | 'peru' | 'chile' | 'ghana' | 'senegal' | 'south_africa' | 'indonesia' | 'singapore'
  | 'romania' | 'bulgaria' | 'czech_republic' | 'hungary'
  | 'ireland' | 'austria' | 'finland' | 'taiwan' | 'hong_kong' | 'new_zealand'
  | 'pakistan' | 'bahrain' | 'tunisia' | 'costa_rica' | 'dominican_republic';

/**
 * For each language, maps translated slug → country key in COUNTRY_PAGES
 * English slugs always work as fallback (handled in resolver).
 */
export const TRANSLATED_COUNTRY_SLUGS: Record<string, Record<string, CountryKey>> = {
  // ─── FRENCH ────────────────────────────────────────────
  fr: {
    'centre-appel-maroc': 'morocco',
    'centre-appel-france': 'france',
    'centre-appel-allemagne': 'germany',
    'centre-appel-espagne': 'spain',
    'centre-appel-royaume-uni': 'uk',
    'centre-appel-etats-unis': 'usa',
    'centre-appel-canada': 'canada',
    'centre-appel-pays-bas': 'netherlands',
    'centre-appel-belgique': 'belgium',
    'centre-appel-suisse': 'switzerland',
    'centre-appel-italie': 'italy',
    'centre-appel-emirats': 'uae',
    'centre-appel-arabie-saoudite': 'saudi-arabia',
    'centre-appel-qatar': 'qatar',
    'centre-appel-scandinavie': 'scandinavia',
    'centre-appel-portugal': 'portugal',
    'centre-appel-japon': 'japan',
    'centre-appel-coree': 'korea',
    'centre-appel-turquie': 'turkey',
    'centre-appel-bresil': 'brazil',
    'centre-appel-inde': 'india',
    'centre-appel-australie': 'australia',
    'centre-appel-afrique-du-sud': 'south-africa',
    'centre-appel-mexique': 'mexico',
    'centre-appel-colombie': 'colombia',
    'centre-appel-pologne': 'poland',
    'centre-appel-philippines': 'philippines',
    'centre-appel-nigeria': 'nigeria',
    'centre-appel-egypte': 'egypt',
    'centre-appel-ksa': 'saudi-arabia-expanded',
    'centre-appel-vietnam': 'vietnam',
    'centre-appel-grece': 'greece',
    'centre-appel-kenya': 'kenya',
    'centre-appel-thailande': 'thailand',
    'centre-appel-malaisie': 'malaysia',
    'centre-appel-argentine': 'argentina',
    'centre-appel-ukraine': 'ukraine',
    'centre-appel-suede': 'sweden',
    'centre-appel-danemark': 'denmark',
    'centre-appel-russie': 'russia',
    'centre-appel-chine': 'china',
    'centre-appel-perou': 'peru',
    'centre-appel-chili': 'chile',
    'centre-appel-ghana': 'ghana',
    'centre-appel-senegal': 'senegal',
    'centre-appel-afrique-sud': 'south_africa',
    'centre-appel-indonesie': 'indonesia',
    'centre-appel-singapour': 'singapore',
    'centre-appel-roumanie': 'romania',
    'centre-appel-bulgarie': 'bulgaria',
    'centre-appel-republique-tcheque': 'czech_republic',
    'centre-appel-hongrie': 'hungary',
    'centre-appel-irlande': 'ireland',
    'centre-appel-autriche': 'austria',
    'centre-appel-finlande': 'finland',
    'centre-appel-taiwan': 'taiwan',
    'centre-appel-hong-kong': 'hong_kong',
    'centre-appel-nouvelle-zelande': 'new_zealand',
    'centre-appel-pakistan': 'pakistan',
    'centre-appel-bahrein': 'bahrain',
    'centre-appel-tunisie': 'tunisia',
    'centre-appel-costa-rica': 'costa_rica',
    'centre-appel-republique-dominicaine': 'dominican_republic',
  },

  // ─── SPANISH ───────────────────────────────────────────
  es: {
    'centro-llamadas-marruecos': 'morocco',
    'centro-llamadas-francia': 'france',
    'centro-llamadas-alemania': 'germany',
    'centro-llamadas-espana': 'spain',
    'centro-llamadas-reino-unido': 'uk',
    'centro-llamadas-estados-unidos': 'usa',
    'centro-llamadas-canada': 'canada',
    'centro-llamadas-paises-bajos': 'netherlands',
    'centro-llamadas-belgica': 'belgium',
    'centro-llamadas-suiza': 'switzerland',
    'centro-llamadas-italia': 'italy',
    'centro-llamadas-emiratos': 'uae',
    'centro-llamadas-arabia-saudita': 'saudi-arabia',
    'centro-llamadas-qatar': 'qatar',
    'centro-llamadas-escandinavia': 'scandinavia',
    'centro-llamadas-portugal': 'portugal',
    'centro-llamadas-japon': 'japan',
    'centro-llamadas-corea': 'korea',
    'centro-llamadas-turquia': 'turkey',
    'centro-llamadas-brasil': 'brazil',
    'centro-llamadas-india': 'india',
    'centro-llamadas-australia': 'australia',
    'centro-llamadas-sudafrica': 'south-africa',
    'centro-llamadas-mexico': 'mexico',
    'centro-llamadas-colombia': 'colombia',
    'centro-llamadas-polonia': 'poland',
    'centro-llamadas-filipinas': 'philippines',
    'centro-llamadas-nigeria': 'nigeria',
    'centro-llamadas-egipto': 'egypt',
    'centro-llamadas-ksa': 'saudi-arabia-expanded',
    'centro-llamadas-vietnam': 'vietnam',
    'centro-llamadas-grecia': 'greece',
    'centro-llamadas-kenia': 'kenya',
    'centro-llamadas-tailandia': 'thailand',
    'centro-llamadas-malasia': 'malaysia',
    'centro-llamadas-argentina': 'argentina',
    'centro-llamadas-ucrania': 'ukraine',
    'centro-llamadas-suecia': 'sweden',
    'centro-llamadas-dinamarca': 'denmark',
    'centro-llamadas-rusia': 'russia',
    'centro-llamadas-china': 'china',
    'centro-llamadas-peru': 'peru',
    'centro-llamadas-chile': 'chile',
    'centro-llamadas-ghana': 'ghana',
    'centro-llamadas-senegal': 'senegal',
    'centro-llamadas-sudafrica-2': 'south_africa',
    'centro-llamadas-indonesia': 'indonesia',
    'centro-llamadas-singapur': 'singapore',
    'centro-llamadas-rumania': 'romania',
    'centro-llamadas-bulgaria': 'bulgaria',
    'centro-llamadas-republica-checa': 'czech_republic',
    'centro-llamadas-hungria': 'hungary',
    'centro-llamadas-irlanda': 'ireland',
    'centro-llamadas-austria': 'austria',
    'centro-llamadas-finlandia': 'finland',
    'centro-llamadas-taiwan': 'taiwan',
    'centro-llamadas-hong-kong': 'hong_kong',
    'centro-llamadas-nueva-zelanda': 'new_zealand',
    'centro-llamadas-pakistan': 'pakistan',
    'centro-llamadas-bahrein': 'bahrain',
    'centro-llamadas-tunez': 'tunisia',
    'centro-llamadas-costa-rica': 'costa_rica',
    'centro-llamadas-republica-dominicana': 'dominican_republic',
  },

  // ─── GERMAN ────────────────────────────────────────────
  de: {
    'callcenter-marokko': 'morocco',
    'callcenter-frankreich': 'france',
    'callcenter-deutschland': 'germany',
    'callcenter-spanien': 'spain',
    'callcenter-grossbritannien': 'uk',
    'callcenter-usa': 'usa',
    'callcenter-kanada': 'canada',
    'callcenter-niederlande': 'netherlands',
    'callcenter-belgien': 'belgium',
    'callcenter-schweiz': 'switzerland',
    'callcenter-italien': 'italy',
    'callcenter-vae': 'uae',
    'callcenter-saudi-arabien': 'saudi-arabia',
    'callcenter-katar': 'qatar',
    'callcenter-skandinavien': 'scandinavia',
    'callcenter-portugal': 'portugal',
    'callcenter-japan': 'japan',
    'callcenter-korea': 'korea',
    'callcenter-tuerkei': 'turkey',
    'callcenter-brasilien': 'brazil',
    'callcenter-indien': 'india',
    'callcenter-australien': 'australia',
    'callcenter-suedafrika': 'south-africa',
    'callcenter-mexiko': 'mexico',
    'callcenter-kolumbien': 'colombia',
    'callcenter-polen': 'poland',
    'callcenter-philippinen': 'philippines',
    'callcenter-nigeria': 'nigeria',
    'callcenter-aegypten': 'egypt',
    'callcenter-ksa': 'saudi-arabia-expanded',
    'callcenter-vietnam': 'vietnam',
    'callcenter-griechenland': 'greece',
    'callcenter-kenia': 'kenya',
    'callcenter-thailand': 'thailand',
    'callcenter-malaysia': 'malaysia',
    'callcenter-argentinien': 'argentina',
    'callcenter-ukraine': 'ukraine',
    'callcenter-schweden': 'sweden',
    'callcenter-daenemark': 'denmark',
    'callcenter-russland': 'russia',
    'callcenter-china': 'china',
    'callcenter-peru': 'peru',
    'callcenter-chile': 'chile',
    'callcenter-ghana': 'ghana',
    'callcenter-senegal': 'senegal',
    'callcenter-suedafrika-2': 'south_africa',
    'callcenter-indonesien': 'indonesia',
    'callcenter-singapur': 'singapore',
    'callcenter-rumaenien': 'romania',
    'callcenter-bulgarien': 'bulgaria',
    'callcenter-tschechien': 'czech_republic',
    'callcenter-ungarn': 'hungary',
    'callcenter-irland': 'ireland',
    'callcenter-oesterreich': 'austria',
    'callcenter-finnland': 'finland',
    'callcenter-taiwan': 'taiwan',
    'callcenter-hongkong': 'hong_kong',
    'callcenter-neuseeland': 'new_zealand',
    'callcenter-pakistan': 'pakistan',
    'callcenter-bahrain': 'bahrain',
    'callcenter-tunesien': 'tunisia',
    'callcenter-costa-rica': 'costa_rica',
    'callcenter-dominikanische-republik': 'dominican_republic',
  },

  // ─── ITALIAN ───────────────────────────────────────────
  it: {
    'call-center-marocco': 'morocco',
    'call-center-francia': 'france',
    'call-center-germania': 'germany',
    'call-center-spagna': 'spain',
    'call-center-regno-unito': 'uk',
    'call-center-stati-uniti': 'usa',
    'call-center-canada': 'canada',
    'call-center-paesi-bassi': 'netherlands',
    'call-center-belgio': 'belgium',
    'call-center-svizzera': 'switzerland',
    'call-center-italia': 'italy',
    'call-center-emirati': 'uae',
    'call-center-arabia-saudita': 'saudi-arabia',
    'call-center-qatar': 'qatar',
    'call-center-scandinavia': 'scandinavia',
    'call-center-portogallo': 'portugal',
    'call-center-giappone': 'japan',
    'call-center-corea': 'korea',
    'call-center-turchia': 'turkey',
    'call-center-brasile': 'brazil',
    'call-center-india': 'india',
    'call-center-australia': 'australia',
    'call-center-sudafrica': 'south-africa',
    'call-center-messico': 'mexico',
    'call-center-colombia': 'colombia',
    'call-center-polonia': 'poland',
    'call-center-filippine': 'philippines',
    'call-center-nigeria': 'nigeria',
    'call-center-egitto': 'egypt',
    'call-center-ksa': 'saudi-arabia-expanded',
    'call-center-vietnam': 'vietnam',
    'call-center-grecia': 'greece',
    'call-center-kenya': 'kenya',
    'call-center-thailandia': 'thailand',
    'call-center-malesia': 'malaysia',
    'call-center-argentina': 'argentina',
    'call-center-ucraina': 'ukraine',
    'call-center-svezia': 'sweden',
    'call-center-danimarca': 'denmark',
    'call-center-russia': 'russia',
    'call-center-cina': 'china',
    'call-center-peru-it': 'peru',
    'call-center-cile': 'chile',
    'call-center-ghana-it': 'ghana',
    'call-center-senegal-it': 'senegal',
    'call-center-sudafrica-2': 'south_africa',
    'call-center-indonesia-it': 'indonesia',
    'call-center-singapore-it': 'singapore',
    'call-center-romania-it': 'romania',
    'call-center-bulgaria-it': 'bulgaria',
    'call-center-repubblica-ceca': 'czech_republic',
    'call-center-ungheria': 'hungary',
    'call-center-irlanda': 'ireland',
    'call-center-austria-it': 'austria',
    'call-center-finlandia': 'finland',
    'call-center-taiwan-it': 'taiwan',
    'call-center-hong-kong-it': 'hong_kong',
    'call-center-nuova-zelanda': 'new_zealand',
    'call-center-pakistan-it': 'pakistan',
    'call-center-bahrein': 'bahrain',
    'call-center-tunisia-it': 'tunisia',
    'call-center-costa-rica-it': 'costa_rica',
    'call-center-repubblica-dominicana': 'dominican_republic',
  },

  // ─── PORTUGUESE ────────────────────────────────────────
  pt: {
    'call-center-marrocos': 'morocco',
    'call-center-franca': 'france',
    'call-center-alemanha': 'germany',
    'call-center-espanha': 'spain',
    'call-center-reino-unido': 'uk',
    'call-center-estados-unidos': 'usa',
    'call-center-canada': 'canada',
    'call-center-holanda': 'netherlands',
    'call-center-belgica': 'belgium',
    'call-center-suica': 'switzerland',
    'call-center-italia': 'italy',
    'call-center-emirados': 'uae',
    'call-center-arabia-saudita': 'saudi-arabia',
    'call-center-catar': 'qatar',
    'call-center-escandinavia': 'scandinavia',
    'call-center-portugal': 'portugal',
    'call-center-japao': 'japan',
    'call-center-coreia': 'korea',
    'call-center-turquia': 'turkey',
    'call-center-brasil': 'brazil',
    'call-center-india': 'india',
    'call-center-australia': 'australia',
    'call-center-africa-do-sul': 'south-africa',
    'call-center-mexico': 'mexico',
    'call-center-colombia': 'colombia',
    'call-center-polonia': 'poland',
    'call-center-filipinas': 'philippines',
    'call-center-nigeria': 'nigeria',
    'call-center-egito': 'egypt',
    'call-center-ksa': 'saudi-arabia-expanded',
    'call-center-vietna': 'vietnam',
    'call-center-grecia': 'greece',
    'call-center-quenia': 'kenya',
    'call-center-tailandia': 'thailand',
    'call-center-malasia': 'malaysia',
    'call-center-argentina': 'argentina',
    'call-center-ucrania': 'ukraine',
    'call-center-suecia': 'sweden',
    'call-center-dinamarca': 'denmark',
    'call-center-russia': 'russia',
    'call-center-china': 'china',
    'call-center-peru-pt': 'peru',
    'call-center-chile-pt': 'chile',
    'call-center-gana': 'ghana',
    'call-center-senegal-pt': 'senegal',
    'call-center-africa-do-sul-2': 'south_africa',
    'call-center-indonesia-pt': 'indonesia',
    'call-center-singapura': 'singapore',
    'call-center-romenia': 'romania',
    'call-center-bulgaria-pt': 'bulgaria',
    'call-center-republica-checa': 'czech_republic',
    'call-center-hungria': 'hungary',
    'call-center-irlanda-pt': 'ireland',
    'call-center-austria-pt': 'austria',
    'call-center-finlandia-pt': 'finland',
    'call-center-taiwan-pt': 'taiwan',
    'call-center-hong-kong-pt': 'hong_kong',
    'call-center-nova-zelandia': 'new_zealand',
    'call-center-paquistao': 'pakistan',
    'call-center-bahrein-pt': 'bahrain',
    'call-center-tunisia-pt': 'tunisia',
    'call-center-costa-rica-pt': 'costa_rica',
    'call-center-republica-dominicana-pt': 'dominican_republic',
  },

  // ─── DUTCH ─────────────────────────────────────────────
  nl: {
    'callcenter-marokko': 'morocco',
    'callcenter-frankrijk': 'france',
    'callcenter-duitsland': 'germany',
    'callcenter-spanje': 'spain',
    'callcenter-verenigd-koninkrijk': 'uk',
    'callcenter-amerika': 'usa',
    'callcenter-canada': 'canada',
    'callcenter-nederland': 'netherlands',
    'callcenter-belgie': 'belgium',
    'callcenter-zwitserland': 'switzerland',
    'callcenter-italie': 'italy',
    'callcenter-vae': 'uae',
    'callcenter-saoedi-arabie': 'saudi-arabia',
    'callcenter-qatar': 'qatar',
    'callcenter-scandinavie': 'scandinavia',
    'callcenter-portugal': 'portugal',
    'callcenter-japan': 'japan',
    'callcenter-korea': 'korea',
    'callcenter-turkije': 'turkey',
    'callcenter-brazilie': 'brazil',
    'callcenter-india': 'india',
    'callcenter-australie': 'australia',
    'callcenter-zuid-afrika': 'south-africa',
    'callcenter-mexico': 'mexico',
    'callcenter-colombia': 'colombia',
    'callcenter-polen': 'poland',
    'callcenter-filipijnen': 'philippines',
    'callcenter-nigeria': 'nigeria',
    'callcenter-egypte': 'egypt',
    'callcenter-ksa': 'saudi-arabia-expanded',
    'callcenter-vietnam': 'vietnam',
    'callcenter-griekenland': 'greece',
    'callcenter-kenia': 'kenya',
    'callcenter-thailand': 'thailand',
    'callcenter-maleisie': 'malaysia',
    'callcenter-argentinie': 'argentina',
    'callcenter-oekraine': 'ukraine',
    'callcenter-zweden': 'sweden',
    'callcenter-denemarken': 'denmark',
    'callcenter-rusland': 'russia',
    'callcenter-china': 'china',
    'callcenter-peru-nl': 'peru',
    'callcenter-chili': 'chile',
    'callcenter-ghana-nl': 'ghana',
    'callcenter-senegal-nl': 'senegal',
    'callcenter-zuid-afrika-2': 'south_africa',
    'callcenter-indonesie': 'indonesia',
    'callcenter-singapore-nl': 'singapore',
    'callcenter-roemenie': 'romania',
    'callcenter-bulgarije': 'bulgaria',
    'callcenter-tsjechie': 'czech_republic',
    'callcenter-hongarije': 'hungary',
    'callcenter-ierland': 'ireland',
    'callcenter-oostenrijk': 'austria',
    'callcenter-finland-nl': 'finland',
    'callcenter-taiwan-nl': 'taiwan',
    'callcenter-hongkong-nl': 'hong_kong',
    'callcenter-nieuw-zeeland': 'new_zealand',
    'callcenter-pakistan-nl': 'pakistan',
    'callcenter-bahrein-nl': 'bahrain',
    'callcenter-tunesie': 'tunisia',
    'callcenter-costa-rica-nl': 'costa_rica',
    'callcenter-dominicaanse-republiek': 'dominican_republic',
  },

  // ─── POLISH ────────────────────────────────────────────
  pl: {
    'call-center-maroko': 'morocco',
    'call-center-francja': 'france',
    'call-center-niemcy': 'germany',
    'call-center-hiszpania': 'spain',
    'call-center-wielka-brytania': 'uk',
    'call-center-stany-zjednoczone': 'usa',
    'call-center-kanada': 'canada',
    'call-center-holandia': 'netherlands',
    'call-center-belgia': 'belgium',
    'call-center-szwajcaria': 'switzerland',
    'call-center-wlochy': 'italy',
    'call-center-zea': 'uae',
    'call-center-arabia-saudyjska': 'saudi-arabia',
    'call-center-katar': 'qatar',
    'call-center-skandynawia': 'scandinavia',
    'call-center-portugalia': 'portugal',
    'call-center-japonia': 'japan',
    'call-center-korea': 'korea',
    'call-center-turcja': 'turkey',
    'call-center-brazylia': 'brazil',
    'call-center-indie': 'india',
    'call-center-australia': 'australia',
    'call-center-republika-poludniowej-afryki': 'south-africa',
    'call-center-meksyk': 'mexico',
    'call-center-kolumbia': 'colombia',
    'call-center-polska': 'poland',
    'call-center-filipiny': 'philippines',
    'call-center-nigeria': 'nigeria',
    'call-center-egipt': 'egypt',
    'call-center-ksa': 'saudi-arabia-expanded',
    'call-center-wietnam': 'vietnam',
    'call-center-grecja': 'greece',
    'call-center-kenia': 'kenya',
    'call-center-tajlandia': 'thailand',
    'call-center-malezja': 'malaysia',
    'call-center-argentyna': 'argentina',
    'call-center-ukraina': 'ukraine',
    'call-center-szwecja': 'sweden',
    'call-center-dania': 'denmark',
    'call-center-rosja': 'russia',
    'call-center-chiny': 'china',
    'call-center-peru-pl': 'peru',
    'call-center-chile-pl': 'chile',
    'call-center-ghana-pl': 'ghana',
    'call-center-senegal-pl': 'senegal',
    'call-center-rpa-2': 'south_africa',
    'call-center-indonezja': 'indonesia',
    'call-center-singapur-pl': 'singapore',
    'call-center-rumunia': 'romania',
    'call-center-bulgaria-pl': 'bulgaria',
    'call-center-czechy': 'czech_republic',
    'call-center-wegry': 'hungary',
    'call-center-irlandia': 'ireland',
    'call-center-austria-pl': 'austria',
    'call-center-finlandia-pl': 'finland',
    'call-center-tajwan': 'taiwan',
    'call-center-hongkong-pl': 'hong_kong',
    'call-center-nowa-zelandia': 'new_zealand',
    'call-center-pakistan-pl': 'pakistan',
    'call-center-bahrajn': 'bahrain',
    'call-center-tunezja': 'tunisia',
    'call-center-kostaryka': 'costa_rica',
    'call-center-dominikana': 'dominican_republic',
  },

  // ─── TURKISH ───────────────────────────────────────────
  tr: {
    'cagri-merkezi-fas': 'morocco',
    'cagri-merkezi-fransa': 'france',
    'cagri-merkezi-almanya': 'germany',
    'cagri-merkezi-ispanya': 'spain',
    'cagri-merkezi-ingiltere': 'uk',
    'cagri-merkezi-abd': 'usa',
    'cagri-merkezi-kanada': 'canada',
    'cagri-merkezi-hollanda': 'netherlands',
    'cagri-merkezi-belcika': 'belgium',
    'cagri-merkezi-isvicre': 'switzerland',
    'cagri-merkezi-italya': 'italy',
    'cagri-merkezi-bae': 'uae',
    'cagri-merkezi-suudi-arabistan': 'saudi-arabia',
    'cagri-merkezi-katar': 'qatar',
    'cagri-merkezi-iskandinavya': 'scandinavia',
    'cagri-merkezi-portekiz': 'portugal',
    'cagri-merkezi-japonya': 'japan',
    'cagri-merkezi-kore': 'korea',
    'cagri-merkezi-turkiye': 'turkey',
    'cagri-merkezi-brezilya': 'brazil',
    'cagri-merkezi-hindistan': 'india',
    'cagri-merkezi-avustralya': 'australia',
    'cagri-merkezi-guney-afrika': 'south-africa',
    'cagri-merkezi-meksika': 'mexico',
    'cagri-merkezi-kolombiya': 'colombia',
    'cagri-merkezi-polonya': 'poland',
    'cagri-merkezi-filipinler': 'philippines',
    'cagri-merkezi-nijerya': 'nigeria',
    'cagri-merkezi-misir': 'egypt',
    'cagri-merkezi-ksa': 'saudi-arabia-expanded',
    'cagri-merkezi-vietnam': 'vietnam',
    'cagri-merkezi-yunanistan': 'greece',
    'cagri-merkezi-kenya': 'kenya',
    'cagri-merkezi-tayland': 'thailand',
    'cagri-merkezi-malezya': 'malaysia',
    'cagri-merkezi-arjantin': 'argentina',
    'cagri-merkezi-ukrayna': 'ukraine',
    'cagri-merkezi-isvec': 'sweden',
    'cagri-merkezi-danimarka': 'denmark',
    'cagri-merkezi-rusya': 'russia',
    'cagri-merkezi-cin': 'china',
    'cagri-merkezi-peru': 'peru',
    'cagri-merkezi-sili': 'chile',
    'cagri-merkezi-gana': 'ghana',
    'cagri-merkezi-senegal-tr': 'senegal',
    'cagri-merkezi-guney-afrika-2': 'south_africa',
    'cagri-merkezi-endonezya': 'indonesia',
    'cagri-merkezi-singapur': 'singapore',
    'cagri-merkezi-romanya': 'romania',
    'cagri-merkezi-bulgaristan': 'bulgaria',
    'cagri-merkezi-cek-cumhuriyeti': 'czech_republic',
    'cagri-merkezi-macaristan': 'hungary',
    'cagri-merkezi-irlanda': 'ireland',
    'cagri-merkezi-avusturya': 'austria',
    'cagri-merkezi-finlandiya': 'finland',
    'cagri-merkezi-tayvan': 'taiwan',
    'cagri-merkezi-hong-kong': 'hong_kong',
    'cagri-merkezi-yeni-zelanda': 'new_zealand',
    'cagri-merkezi-pakistan': 'pakistan',
    'cagri-merkezi-bahreyn': 'bahrain',
    'cagri-merkezi-tunus': 'tunisia',
    'cagri-merkezi-kosta-rika': 'costa_rica',
    'cagri-merkezi-dominik-cumhuriyeti': 'dominican_republic',
  },

  // ─── SWEDISH ───────────────────────────────────────────
  sv: {
    'callcenter-marocko': 'morocco',
    'callcenter-frankrike': 'france',
    'callcenter-tyskland': 'germany',
    'callcenter-spanien': 'spain',
    'callcenter-storbritannien': 'uk',
    'callcenter-usa': 'usa',
    'callcenter-kanada': 'canada',
    'callcenter-nederlaenderna': 'netherlands',
    'callcenter-belgien': 'belgium',
    'callcenter-schweiz': 'switzerland',
    'callcenter-italien': 'italy',
    'callcenter-foerenade-arabemiraten': 'uae',
    'callcenter-saudiarabien': 'saudi-arabia',
    'callcenter-qatar': 'qatar',
    'callcenter-skandinavien': 'scandinavia',
    'callcenter-portugal': 'portugal',
    'callcenter-japan': 'japan',
    'callcenter-sydkorea': 'korea',
    'callcenter-turkiet': 'turkey',
    'callcenter-brasilien': 'brazil',
    'callcenter-indien': 'india',
    'callcenter-australien': 'australia',
    'callcenter-sydafrika': 'south-africa',
    'callcenter-mexiko': 'mexico',
    'callcenter-colombia': 'colombia',
    'callcenter-polen': 'poland',
    'callcenter-filippinerna': 'philippines',
    'callcenter-nigeria': 'nigeria',
    'callcenter-egypten': 'egypt',
    'callcenter-ksa': 'saudi-arabia-expanded',
    'callcenter-vietnam': 'vietnam',
    'callcenter-grekland': 'greece',
    'callcenter-kenya': 'kenya',
    'callcenter-thailand': 'thailand',
    'callcenter-malaysia': 'malaysia',
    'callcenter-argentina': 'argentina',
    'callcenter-ukraina': 'ukraine',
    'callcenter-sverige': 'sweden',
    'callcenter-danmark': 'denmark',
    'callcenter-ryssland': 'russia',
    'callcenter-kina': 'china',
    'callcenter-peru-sv': 'peru',
    'callcenter-chile-sv': 'chile',
    'callcenter-ghana-sv': 'ghana',
    'callcenter-senegal-sv': 'senegal',
    'callcenter-sydafrika-2': 'south_africa',
    'callcenter-indonesien-sv': 'indonesia',
    'callcenter-singapore-sv': 'singapore',
    'callcenter-rumanien': 'romania',
    'callcenter-bulgarien-sv': 'bulgaria',
    'callcenter-tjeckien': 'czech_republic',
    'callcenter-ungern': 'hungary',
    'callcenter-irland-sv': 'ireland',
    'callcenter-oesterrike': 'austria',
    'callcenter-finland-sv': 'finland',
    'callcenter-taiwan-sv': 'taiwan',
    'callcenter-hongkong-sv': 'hong_kong',
    'callcenter-nya-zeeland': 'new_zealand',
    'callcenter-pakistan-sv': 'pakistan',
    'callcenter-bahrain-sv': 'bahrain',
    'callcenter-tunisien': 'tunisia',
    'callcenter-costa-rica-sv': 'costa_rica',
    'callcenter-dominikanska-republiken': 'dominican_republic',
  },

  // ─── DANISH ────────────────────────────────────────────
  da: {
    'callcenter-marokko': 'morocco',
    'callcenter-frankrig': 'france',
    'callcenter-tyskland': 'germany',
    'callcenter-spanien': 'spain',
    'callcenter-storbritannien': 'uk',
    'callcenter-usa': 'usa',
    'callcenter-canada': 'canada',
    'callcenter-holland': 'netherlands',
    'callcenter-belgien': 'belgium',
    'callcenter-schweiz': 'switzerland',
    'callcenter-italien': 'italy',
    'callcenter-forenede-arabiske-emirater': 'uae',
    'callcenter-saudi-arabien': 'saudi-arabia',
    'callcenter-qatar': 'qatar',
    'callcenter-skandinavien': 'scandinavia',
    'callcenter-portugal': 'portugal',
    'callcenter-japan': 'japan',
    'callcenter-sydkorea': 'korea',
    'callcenter-tyrkiet': 'turkey',
    'callcenter-brasilien': 'brazil',
    'callcenter-indien': 'india',
    'callcenter-australien': 'australia',
    'callcenter-sydafrika': 'south-africa',
    'callcenter-mexico': 'mexico',
    'callcenter-colombia': 'colombia',
    'callcenter-polen': 'poland',
    'callcenter-filippinerne': 'philippines',
    'callcenter-nigeria': 'nigeria',
    'callcenter-aegypten': 'egypt',
    'callcenter-ksa': 'saudi-arabia-expanded',
    'callcenter-vietnam': 'vietnam',
    'callcenter-graekenland': 'greece',
    'callcenter-kenya': 'kenya',
    'callcenter-thailand': 'thailand',
    'callcenter-malaysia': 'malaysia',
    'callcenter-argentina': 'argentina',
    'callcenter-ukraine': 'ukraine',
    'callcenter-sverige': 'sweden',
    'callcenter-danmark': 'denmark',
    'callcenter-rusland': 'russia',
    'callcenter-kina': 'china',
    'callcenter-peru-da': 'peru',
    'callcenter-chile-da': 'chile',
    'callcenter-ghana-da': 'ghana',
    'callcenter-senegal-da': 'senegal',
    'callcenter-sydafrika-da': 'south_africa',
    'callcenter-indonesien-da': 'indonesia',
    'callcenter-singapore-da': 'singapore',
    'callcenter-rumaenien-da': 'romania',
    'callcenter-bulgarien-da': 'bulgaria',
    'callcenter-tjekkiet': 'czech_republic',
    'callcenter-ungarn-da': 'hungary',
    'callcenter-irland-da': 'ireland',
    'callcenter-oestrig': 'austria',
    'callcenter-finland-da': 'finland',
    'callcenter-taiwan-da': 'taiwan',
    'callcenter-hongkong-da': 'hong_kong',
    'callcenter-new-zealand-da': 'new_zealand',
    'callcenter-pakistan-da': 'pakistan',
    'callcenter-bahrain-da': 'bahrain',
    'callcenter-tunesien-da': 'tunisia',
    'callcenter-costa-rica-da': 'costa_rica',
    'callcenter-den-dominikanske-republik': 'dominican_republic',
  },
};

/**
 * Build a reverse lookup: any slug (English or translated) → country key
 * Includes all English slugs as universal fallback.
 */
const ENGLISH_SLUG_MAP: Record<string, CountryKey> = {
  'call-center-for-morocco': 'morocco',
  'call-center-for-france': 'france',
  'call-center-for-germany': 'germany',
  'call-center-for-spain': 'spain',
  'call-center-for-uk': 'uk',
  'call-center-for-usa': 'usa',
  'call-center-for-canada': 'canada',
  'call-center-for-netherlands': 'netherlands',
  'call-center-for-belgium': 'belgium',
  'call-center-for-switzerland': 'switzerland',
  'call-center-for-italy': 'italy',
  'call-center-for-uae': 'uae',
  'call-center-for-saudi-arabia': 'saudi-arabia',
  'call-center-for-qatar': 'qatar',
  'call-center-for-scandinavia': 'scandinavia',
  'call-center-for-portugal': 'portugal',
  'call-center-for-japan': 'japan',
  'call-center-for-korea': 'korea',
  'call-center-for-turkey': 'turkey',
  'call-center-for-brazil': 'brazil',
  'call-center-for-india': 'india',
  'call-center-for-australia': 'australia',
  'call-center-for-south-africa': 'south-africa',
  'call-center-for-mexico': 'mexico',
  'call-center-for-colombia': 'colombia',
  'call-center-for-poland': 'poland',
  'call-center-for-philippines': 'philippines',
  'call-center-for-nigeria': 'nigeria',
  'call-center-for-egypt': 'egypt',
  'call-center-for-ksa': 'saudi-arabia-expanded',
  'call-center-for-vietnam': 'vietnam',
  'call-center-for-greece': 'greece',
  'call-center-for-kenya': 'kenya',
  'call-center-for-thailand': 'thailand',
  'call-center-for-malaysia': 'malaysia',
  'call-center-for-argentina': 'argentina',
  'call-center-for-ukraine': 'ukraine',
  'call-center-for-sweden': 'sweden',
  'call-center-for-denmark': 'denmark',
  'call-center-for-russia': 'russia',
  'call-center-for-china': 'china',
  'call-center-for-peru': 'peru',
  'call-center-for-chile': 'chile',
  'call-center-for-ghana': 'ghana',
  'call-center-for-senegal': 'senegal',
  'call-center-for-south-africa-2': 'south_africa',
  'call-center-for-indonesia': 'indonesia',
  'call-center-for-singapore': 'singapore',
  'call-center-for-romania': 'romania',
  'call-center-for-bulgaria': 'bulgaria',
  'call-center-for-czech-republic': 'czech_republic',
  'call-center-for-hungary': 'hungary',
  'call-center-for-ireland': 'ireland',
  'call-center-for-austria': 'austria',
  'call-center-for-finland': 'finland',
  'call-center-for-taiwan': 'taiwan',
  'call-center-for-hong-kong': 'hong_kong',
  'call-center-for-new-zealand': 'new_zealand',
  'call-center-for-pakistan': 'pakistan',
  'call-center-for-bahrain': 'bahrain',
  'call-center-for-tunisia': 'tunisia',
  'call-center-for-costa-rica': 'costa_rica',
  'call-center-for-dominican-republic': 'dominican_republic',
};

/**
 * Resolves any slug (translated or English) to a country key.
 * Checks language-specific translated slugs first, then falls back to English.
 */
export function resolveCountrySlug(slug: string, language: string): CountryKey | undefined {
  // Check translated slugs for current language
  const langSlugs = TRANSLATED_COUNTRY_SLUGS[language];
  if (langSlugs && langSlugs[slug]) {
    return langSlugs[slug];
  }
  // Fallback to English slug map (works for all languages including ar, zh, ja, ko, ru)
  return ENGLISH_SLUG_MAP[slug];
}

/**
 * Get the translated slug for a country in a specific language.
 * Used for generating internal links in the correct language.
 */
export function getTranslatedCountrySlug(countryKey: CountryKey, language: string): string {
  const langSlugs = TRANSLATED_COUNTRY_SLUGS[language];
  if (langSlugs) {
    const entry = Object.entries(langSlugs).find(([, key]) => key === countryKey);
    if (entry) return entry[0];
  }
  // Fallback to English slug
  const enEntry = Object.entries(ENGLISH_SLUG_MAP).find(([, key]) => key === countryKey);
  return enEntry ? enEntry[0] : `call-center-for-${countryKey}`;
}

/**
 * Get ALL slugs (English + all translated) for generating routes.
 */
export function getAllCountrySlugs(): string[] {
  const slugs = new Set<string>();
  // English slugs
  Object.keys(ENGLISH_SLUG_MAP).forEach(s => slugs.add(s));
  // Translated slugs
  Object.values(TRANSLATED_COUNTRY_SLUGS).forEach(langMap => {
    Object.keys(langMap).forEach(s => slugs.add(s));
  });
  return Array.from(slugs);
}
